import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { customerPortalApi } from '../services/apiInstances';
import { deduplicateRequest } from '../utils/requestCache';

interface CustomerAccount {
  customerAccountId: number;
  customerId: number;
  phone: string;
  email?: string;
}

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  company_name?: string;
  email: string;
  phone: string;
  created_at: string;
}

interface CustomerData {
  customerAccount: CustomerAccount;
  customer: Customer;
  stats: {
    vehicles: number;
    estimates: number;
  };
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface CustomerAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  customerData: CustomerData | null;
  accessToken: string | null;
  login: (identifier: string, password: string) => Promise<void>;
  /**
   * `customerId` is optional: supplying one means "link me to this existing record"
   * and requires a portal token the shop issued, which this screen does not have.
   * Resolves with `signedIn: false` when the server accepted the registration but
   * deliberately withheld a session (self-registration).
   */
  register: (options: { phone?: string; email?: string; password: string; customerId?: number; referralCode?: string }) => Promise<{ signedIn: boolean }>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

const TOKEN_KEY = 'customer_access_token';
const REFRESH_TOKEN_KEY = 'customer_refresh_token';

export const CustomerAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const loadingRef = useRef(false); // Prevent concurrent initAuth calls

  // Initialize auth state on mount with deduplication and abort controller
  const initAuth = useCallback(async () => {
    // Prevent concurrent calls (important for React StrictMode)
    if (loadingRef.current) {
      console.log('[CustomerAuthContext] initAuth already in progress, skipping...');
      return;
    }

    loadingRef.current = true;

    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (token && refreshToken) {
      setAccessToken(token);
      try {
        console.log('[CustomerAuthContext] Loading customer data...');

        // Deduplicate the request
        const data = await deduplicateRequest('customer-auth:me', async () => {
          const response = await customerPortalApi.get('/customer-auth/me');
          return response.data;
        });

        setCustomerData(data);
        setIsAuthenticated(true);
      } catch (error: any) {
        console.error('[CustomerAuthContext] Failed to load customer auth:', error);

        // Don't clear tokens on rate limit errors
        if (!error.isRateLimit && error.response?.status !== 429) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
      }
    } else {
      console.log('[CustomerAuthContext] No customer tokens found');
    }

    setIsLoading(false);
    loadingRef.current = false;
  }, []);

  // Load auth state on mount with cleanup
  useEffect(() => {
    const abortController = new AbortController();

    initAuth();

    return () => {
      // Cleanup: abort any in-flight requests
      abortController.abort();
    };
  }, [initAuth]);

  const login = async (identifier: string, password: string) => {
    try {
      const response = await customerPortalApi.post('/customer-auth/login', {
        identifier,
        password,
      });

      const { accessToken, refreshToken, customerAccount, customer } = response.data;

      // Store tokens
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      setAccessToken(accessToken);

      // Set auth state
      setIsAuthenticated(true);
      setCustomerData(response.data);
    } catch (error: any) {
      console.error('Customer login error:', error);
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  /**
   * Register, and sign in only if the server actually handed back a session.
   *
   * `POST /customer-auth/register` has TWO success answers (routes/customer-auth.ts):
   *  - 201 with tokens, when a portal link proved which record is being claimed;
   *  - 202 `REGISTRATION_ACCEPTED` with NO tokens, for self-registration — it
   *    deliberately never returns a session, so the endpoint cannot be used to tell
   *    whether an address is already taken.
   *
   * This used to destructure the tokens unconditionally, so the 202 path stored the
   * string "undefined" as the access token and set `isAuthenticated` anyway. The
   * customer was "logged in" with a token every request rejected — which looks
   * exactly like "I made an account and then login failed".
   *
   * Returns whether a session was established, so the caller can either navigate to
   * the dashboard or tell them to sign in.
   */
  const register = async (options: { phone?: string; email?: string; password: string; customerId?: number; referralCode?: string }): Promise<{ signedIn: boolean }> => {
    try {
      const response = await customerPortalApi.post('/customer-auth/register', options);

      const { accessToken, refreshToken } = response.data ?? {};
      if (!accessToken || !refreshToken) {
        return { signedIn: false };
      }

      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      setAccessToken(accessToken);

      setIsAuthenticated(true);
      setCustomerData(response.data);
      return { signedIn: true };
    } catch (error: any) {
      console.error('Customer registration error:', error);
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        await customerPortalApi.post('/customer-auth/logout', {
          refreshToken,
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens and state regardless of API call success
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setAccessToken(null);
      setIsAuthenticated(false);
      setCustomerData(null);
    }
  };

  const refreshAuth = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        const response = await customerPortalApi.get('/customer-auth/me');

        setCustomerData(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh customer data:', error);
    }
  };

  return (
    <CustomerAuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        customerData,
        accessToken,
        login,
        register,
        logout,
        refreshAuth,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};
